import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getProperties } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Home, MapPin, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import AddPropertyModal from '../components/modals/AddPropertyModal';

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const data = await getProperties();
      setProperties(data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="properties-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Properties</h1>
            <p className="text-gray-600">Manage your property portfolio</p>
          </div>
          <Button data-testid="add-property-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Property
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : properties.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <Home className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-4">No properties yet</p>
              <Button data-testid="add-first-property-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                Add Your First Property
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card key={property.id} data-testid={`property-card-${property.id}`} className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-lg font-semibold text-gray-900 mb-1">{property.address}</div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <MapPin className="w-4 h-4" />
                        {property.suburb}, {property.state} {property.postcode}
                      </div>
                    </div>
                    <span className={`badge ${property.status === 'leased' ? 'badge-success' : property.status === 'maintenance' ? 'badge-warning' : 'badge-info'}`}>
                      {property.status}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium text-gray-900 capitalize">{property.property_type}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Bedrooms:</span>
                      <span className="font-medium text-gray-900">{property.bedrooms} bed | {property.bathrooms} bath</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t">
                      <span className="text-gray-600">Rent:</span>
                      <div className="flex items-center gap-1 font-bold text-blue-600">
                        <DollarSign className="w-4 h-4" />
                        {property.rent_amount} / {property.rent_frequency}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AddPropertyModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadProperties}
        />
      </div>
    </Layout>
  );
}